import React, { useEffect, useMemo, useState, useContext } from 'react';
import {  Row, Button, Dropdown, Layout, Menu, Divider } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useAuthState } from '../../hooks/auth/useAuthState';
import { useTranslations } from '../../hooks/translations/useTranslations';
import { HeaderLogo } from 'app/components/common';
import { AvatarIcon, Link } from '../common';
import { Translate } from '../../services';
import { LoadingOutlined, QuestionCircleOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
const { Header } = Layout;
import { Routes } from '../../navigation';
import { CategoryUtils } from '../../utils';
import { ACTIVE_BRAND_CONFIG, ACTIVE_THEME_NAME, IS_SEIKO_THEME, ThemeContext } from '../../branding';
import classNames from 'classnames';
import ProfileDropdown from './ProfileDropdown';
import { useCategoryListQuery } from '../../gql/hooks';
import CaretIcon from '../common/caretIcon';
import './styles.less';
import { useMediaQuery } from 'react-responsive';
import { NotificationDropdown } from './NotificationDropdown';
import { FeatureFlag } from '../../featureFlags';
import { flags } from '../../featureFlags/flags';

export const AppHeader = () => {
    const { locale, getString } = useTranslations();
    const theme = useContext( ThemeContext );
    const [ dropdownBrand, setDropdownBrand ] = useState( ACTIVE_THEME_NAME );
    const [ catOpen, setCatOpen ] = useState( false );
    const [ profOpen, setProfOpen ] = useState( false ); 
    const [ catOpenMobile, setCatOpenMobile ] = useState( false ); 
    const [ burgerOpenMobile, setBurgerOpenMobile ] = useState( false ); 
    const [ notisOpen, setNotisOpen ] = useState( false );
    const [ loggingOut, setLoggingOut ] = useState( false );
    const { access_token, claims: { name } } = useAuthState();
    const { data: validCategories, loading } = useCategoryListQuery();


    const isSmallLogo = useMediaQuery( { query: '(max-width: 1200px)' } );
    
    const isAuthenticated = useMemo( () => Boolean( access_token ), [ access_token ] );

    const categoryItems = useMemo( () => {
        if ( !validCategories || loading ) {
            return [];
        }

        return validCategories
            .sort( ( a, b ) => ( a.node.sortOrder ?? 0 ) - ( b.node.sortOrder ?? 0 ) )
            .map( c => ( {
                id: CategoryUtils.getId( c ),
                label: CategoryUtils.getName( c, { localeCode: locale } ) || 'Unknown',
            } ) );
    }, [ validCategories, locale, loading ] );

    useEffect( () => {
        window.changeBrand = ( brand ) => {
            window.changeAppBrand( brand );
            setDropdownBrand( brand );
        };
    }, [] );

    const profileDropdown = <ProfileDropdown closeDropdown={ () => setProfOpen( false ) } setLoggingOut={ setLoggingOut } loggingOut={ loggingOut } />;
    
    const categoryList = ( 
        <Menu>
            { loading
                ? <Menu.Item key='loading' className='loading'>
                    <LoadingOutlined className='secondary' />
                </Menu.Item>
                : categoryItems == null && categoryItems.length == 0
                ? <Menu.Item key='nodata'>
                    { getString( 'categoryList.noData' ) }
                </Menu.Item>
                : categoryItems.map( opt => 
                    <Menu.Item key={ opt.id } >
                        <Link push to={ Routes.CategoryDetails.buildPath( opt.id ) } onClick={ () => setCatOpen( false ) }>
                            { opt.label }
                        </Link>
                    </Menu.Item> ) 
            }
        </Menu> );

        
    const categoryListMobile = ( 
        <Menu>
            { categoryItems === null || loading || categoryItems.length == 0
                ? <Menu.Item key='loading' className='loading'>
                    <LoadingOutlined className='secondary' />
                </Menu.Item>
                : categoryItems.map( opt => 
                    <Menu.Item key={ opt.id } >
                        <Link push to={ Routes.CategoryDetails.buildPath( opt.id ) } onClick={ () => setCatOpenMobile( false ) }>
                            { opt.label }
                        </Link>
                    </Menu.Item> ) 
            }
        </Menu> );

    const burgerMenu = (
        <Menu  className={ classNames( theme, 'burger-menu' ) } >   


            { !IS_SEIKO_THEME && 
                <>
                    <Menu.Item key="my-progress" onClick={ e => setBurgerOpenMobile( false ) }>
                        <Link push to={ Routes.Home.buildPath() }>
                            <Translate.Text value="header.nav.myProgress" />
                        </Link>
                    </Menu.Item> 
                    
                    <Menu.Item className='divider' key='div0'><Divider/></Menu.Item>
                </>
            }   
        

            <Menu.Item key="my-learning" onClick={ e => setBurgerOpenMobile( false ) }>
                <Link push to={ Routes.Learning.buildPath() }>
                    <Translate.Text value="header.nav.myLearning" />
                </Link>
            </Menu.Item> 
            
            <Menu.Item className='divider' key='div1'><Divider/></Menu.Item>
           
            <Menu.Item 
                key="categories" 
            >
                <Dropdown 
                    trigger={ [ 'click' ] }
                    onVisibleChange={ ( flag ) => {
                        setCatOpenMobile( flag );
                        if ( flag ) {
                            setProfOpen( false );
                            setNotisOpen( false );
                            setBurgerOpenMobile( false );
                        }
                    } }
                    visible={ catOpenMobile }
                    overlay={ categoryListMobile }
                    overlayClassName={ classNames( 'dropdown', 'category-dropdown', dropdownBrand ) }
                >
                    <a onClick={ e => e.preventDefault() }>
                        <Translate.Text value="header.nav.categories" />
                        <CaretIcon direction={ catOpenMobile ? 'down' : 'right' } />
                    </a>
                </Dropdown>
 
            </Menu.Item>
            
            <Menu.Item className='divider' key='div28'><Divider/></Menu.Item>
             
            <Menu.Item className={ classNames( theme ) } key="search"  onClick={ e => setBurgerOpenMobile( false ) }>
                <Link push to={ Routes.Search.buildPath() }>
                    <SearchOutlined />
                    <Translate.Text value="header.nav.searchLibrary" />
                </Link>
            </Menu.Item> 
        </Menu>
    );

    return (
        <Header className="app-header">
            <Row className="header-row"> 


                <HeaderLogo theme={ theme } small={ isSmallLogo }/> 

                { isAuthenticated &&
                        <Menu className="header-menu text-menu navigation">
                            { /* My Progress */ }
                            { !IS_SEIKO_THEME &&

                                <Menu.Item className={ classNames( 'nav-item', 'underline', 'clickable' ) } key="my-progress" >
                                    <Link push to={ Routes.Home.buildPath() }>
                                        <Translate.Text value="header.nav.myProgress" />
                                    </Link>
                                </Menu.Item>
                            }
                            
                            { /* My Learning */ }
                            <Menu.Item className={ classNames( 'nav-item', 'underline', 'clickable' ) } key="my-learning" >
                                <Link push to={ Routes.Learning.buildPath() }>
                                    <Translate.Text value="header.nav.myLearning" />
                                </Link>
                            </Menu.Item> 

                            { /* Categories */ }
                            <Menu.Item className={ classNames( 'nav-item', 'underline', 'clickable' ) } key="categories">
                                <Dropdown 
                                    trigger={ [ 'click' ] }
                                    onVisibleChange={ ( flag ) => {
                                        setCatOpen( flag );
                                        if ( flag ) {
                                            setProfOpen( false );
                                            setNotisOpen( false );
                                            setBurgerOpenMobile( false );
                                        }
                                    } }
                                    visible={ catOpen }
                                    overlay={ categoryList }
                                    overlayClassName={ classNames( 'dropdown', 'category-dropdown', dropdownBrand ) }
                                >
                                    <a onClick={ e => e.preventDefault() }>
                                        <Translate.Text value="header.nav.categories" />
                                        <CaretIcon direction={ catOpen ? 'down' : 'right' } />
                                    </a>
                                </Dropdown>
                            </Menu.Item>

                            { /* Search */ }
                            <Menu.Item className={ classNames( 'nav-item', 'underline', 'clickable', 'search' ) } key="search" >
                                <Link push to={ Routes.Search.buildPath() }>
                                    <SearchOutlined />
                                    <Translate.Text value="header.nav.searchLibrary" />
                                </Link>
                            </Menu.Item>
                        </Menu>
                    }   

                    <Menu className="header-menu right-menu">


                        <FeatureFlag name={ flags.NOTIFICATIONS }>

                            { /*  noti menu */ }
                            <Menu.Item key='notifications-button' className={ classNames( 'nav-item', 'notifications-button', 'clickable' ) }>
                                <NotificationDropdown
                                    loggingOut={ loggingOut }
                                    visible={ notisOpen }
                                    onVisibleChange={ vis => {
                                        if ( vis ) {
                                            setProfOpen( false ); 
                                            setCatOpen( false );
                                            setNotisOpen( vis );
                                        }
                                    } } />
                            </Menu.Item>

                        </FeatureFlag>
                        
                        
                        { /* Question icon */ }
                        { theme !== 'seiko' && isAuthenticated &&
                            <Menu.Item className={ classNames( 'nav-item', 'icon', 'clickable' ) } key="help">
                                <a href={ `mailto:${ ACTIVE_BRAND_CONFIG.metadata.contactEmail }` }>
                                    <QuestionCircleOutlined aria-label={ getString( 'icons.help' ) } />
                                </a>
                            </Menu.Item>
                        }

                        { /* Mobile hamburger menu */ }
                        <Menu.Item key='burger' className={ classNames( 'nav-item', 'profile', 'clickable', 'burger-button2' ) }>
                            <Dropdown 
                                overlay={ burgerMenu }
                                overlayClassName={ classNames( 'dropdown', 'profile-dropdown' ) }
                                trigger={ [ 'click' ] }
                                visible={ burgerOpenMobile }
                                onVisibleChange={ ( flag ) => {
                                    setBurgerOpenMobile( flag ); 
                                    setProfOpen( false );
                                    setNotisOpen( false );
                                    setCatOpen( false );
                                } }
                            >
                                <Button
                                    className="burger-button" 
                                    type="default" 
                                    size={ 'large' }
                                    onClick={ e => e.preventDefault() }
                                >
                                    <MenuOutlined />
                                </Button>
                            </Dropdown>
                        </Menu.Item>
                        
                        { /* Profile Menu */ }
                        <Menu.Item key='profile' className={ classNames( 'nav-item', 'profile', 'clickable' ) }>
                            <Dropdown 
                                trigger={ [ 'click' ] }
                                onVisibleChange={ ( flag ) => {
                                    setProfOpen( flag );
                                    if ( flag ) {
                                        setCatOpen( false );
                                        setNotisOpen( false );
                                    }
                                } }
                                visible={ profOpen }
                                overlay={ profileDropdown }
                                overlayClassName={ classNames( 'dropdown', 'profile-dropdown', dropdownBrand ) }
                            >

                                <a onClick={ e => e.preventDefault() }>
                                    <AvatarIcon
                                        className="profile-icon"
                                        username={ name ?? null } >
                                        <UserOutlined />
                                    </AvatarIcon>
                                    <CaretIcon direction={ profOpen ? 'down' : 'right' } />
                                </a>
                            </Dropdown>
                        </Menu.Item>
                    </Menu>
            </Row>
        </Header>
    );
};
